/**
 * RoleProfilesTab — the "Org Chart" tab of the Studio workspace (see StudioWorkspace.jsx and
 * docs/ROLE_PROFILES.md).
 *
 * An infinite canvas (Matt, 2026-10-21: "a constellation of nodes that we can scroll, pan, zoom
 * around in"). Roles are nodes laid out as a tree from each role's `reports_to` self-link (derived at
 * read time, never stored). The camera is one { x, y, k } transform on the world layer:
 *   - drag anywhere to pan; a trackpad scroll pans; pinch, Ctrl+scroll or a mouse wheel zooms at the
 *     cursor; two-finger pinch on touch; + / - / 0 (fit) / Esc / "/" (find) on the keyboard
 *   - clicking a role glides the camera to it and opens its profile in a sheet on the right; the
 *     sheet's Reports to / Direct reports chips walk the chart
 *   - the People panel lists everyone on the team with the roles they hold (or "No role yet"), and
 *     hovering a person lights their roles, so open seats and free people are visible together
 *
 * Holders are the real people linked to each role (`role_holders`, database/278). Editing is
 * Principal-tier (hasModule('admin')) through RoleProfileEditModal.
 */

import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react'
import RoleProfileEditModal from './RoleProfileEditModal.jsx'
import { getTeamMembers, getTeamPositions } from '../services/members.js'
import { getCrewRoster, roleHolderTeam } from '../services/flights.js'
import { setRoleHolders, updateRoleProfile } from '../services/roleProfiles.js'

/* ── data helpers ─────────────────────────────────────────────────────────── */

// A ROLE'S TIME (database/328, Matt 2026-10-22, Time & Triage): each role costs its holder a daily
// triage, a focus block and a reserve for emergent work. A person's roles add up against their week;
// the reserve is the LARGEST among their roles, never the sum. What is left is time for their planes.
const WEEK_H = 40
const roleWeekHours = r => (Number(r.triage_minutes) || 0) * 5 / 60 + (Number(r.focus_hours_per_week) || 0)
function weekOf(personRoles) {
  const planned = personRoles.reduce((s, r) => s + roleWeekHours(r), 0)
  const reservePct = personRoles.reduce((m, r) => Math.max(m, Number(r.reserve_pct) || 0), 0)
  const reserve = WEEK_H * reservePct / 100
  return { planned, reservePct, reserve, left: WEEK_H - planned - reserve }
}
const hrs = h => { const v = Math.round(h * 10) / 10; return (Number.isInteger(v) ? v : v.toFixed(1)) + ' h' }

const holdersOf = r => (r && r.holders) || []
const isEmpty = v => v == null || (Array.isArray(v) && v.length === 0)
const missingCoreSections = r => [r.purpose_statement, r.responsibilities, r.decision_authority, r.operational_rhythm, r.success_metrics, r.qualifications].filter(isEmpty).length
const isReconstructedIncomplete = r => r.status !== 'draft' && missingCoreSections(r) >= 4

const FOCUS_LABEL = { pre_flight: 'Owns Pre-Flight on the Flight Board', pre_landing: 'Owns Pre-Landing on the Flight Board' }
const COORD_LABEL = { agency: 'Chases what agencies owe', client: 'Chases what clients owe', consultant: 'Chases what consultants owe', gc: 'Chases what contractors owe' }

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
}

// A calm, fixed palette; a person keeps the same tone everywhere on the chart.
const TONES = ['#3f6f73', '#7a5c8e', '#a0703b', '#4d6b9a', '#8a5a5a', '#5d7a4a', '#6b6b8a', '#2f6a5a']
function toneFor(key) {
  let h = 0
  for (const c of String(key || '')) h = (h * 31 + c.charCodeAt(0)) >>> 0
  return TONES[h % TONES.length]
}

const NODE_W = 236, NODE_H = 128, GAP_X = 44, GAP_Y = 112
const STEP_Y = NODE_H + GAP_Y
const STACK_MIN = 4, STACK_INDENT = 34, STACK_GAP = 18

/**
 * Tree layout in world pixels. A parent centers over its children; when a role has three or more
 * reports that have no reports of their own, those stack in one column under it (the way a staff
 * list hangs off a manager), which keeps the chart compact enough to read at a glance.
 * Returns { pos: Map<key, {x, y, depth}>, childrenOf, stacks: Map<parentKey, { spineX, keys }> }.
 */
function layoutTree(roles) {
  const childrenOf = new Map()
  const keys = new Set(roles.map(r => r.role_key))
  for (const r of roles) {
    const p = r.reports_to && keys.has(r.reports_to) ? r.reports_to : null
    if (!childrenOf.has(p)) childrenOf.set(p, [])
    childrenOf.get(p).push(r)
  }
  for (const arr of childrenOf.values()) arr.sort((a, b) => (a.sort_order - b.sort_order) || a.role_title.localeCompare(b.role_title))
  const kidsOf = k => childrenOf.get(k) || []
  const pos = new Map(), stacks = new Map()

  // Returns the subtree's width; places every node of it.
  function place(role, depth, left, seen) {
    const kids = seen.has(role.role_key) ? [] : kidsOf(role.role_key)
    const y = depth * STEP_Y
    if (!kids.length) { pos.set(role.role_key, { x: left, y, depth }); return NODE_W }
    const next = new Set(seen); next.add(role.role_key)
    const leaves = kids.filter(k => !kidsOf(k.role_key).length)
    const stacked = leaves.length >= STACK_MIN ? leaves : []
    const normal = kids.filter(k => !stacked.includes(k))
    let cursor = left
    const centers = []
    for (const k of normal) {
      const w = place(k, depth + 1, cursor, next)
      const p = pos.get(k.role_key)
      centers.push(p.x + NODE_W / 2)
      cursor += w + GAP_X
    }
    if (stacked.length) {
      const colX = cursor + STACK_INDENT
      stacked.forEach((k, i) => pos.set(k.role_key, { x: colX, y: (depth + 1) * STEP_Y + i * (NODE_H + STACK_GAP), depth: depth + 1 }))
      stacks.set(role.role_key, { spineX: cursor + STACK_INDENT / 2, keys: stacked.map(k => k.role_key) })
      centers.push(cursor + STACK_INDENT / 2)
      cursor += STACK_INDENT + NODE_W + GAP_X
    }
    const width = Math.max(NODE_W, cursor - GAP_X - left)
    const mid = normal.length ? (Math.min(...centers) + Math.max(...centers)) / 2 : left + STACK_INDENT / 2 + NODE_W / 2 - STACK_INDENT / 2
    const x = Math.max(left, Math.min(mid - NODE_W / 2, left + width - NODE_W))
    pos.set(role.role_key, { x, y, depth })
    return width
  }
  let left = 0
  for (const r of childrenOf.get(null) || []) left += place(r, 0, left, new Set()) + GAP_X * 2
  return { pos, childrenOf, stacks }
}
const K_MIN = 0.25, K_MAX = 2.2
const clampK = k => Math.min(K_MAX, Math.max(K_MIN, k))
/** The profile sheet's width: a side sheet on a wide canvas, the whole canvas on a narrow one. */
const sheetWidthFor = el => { const w = el ? el.clientWidth : 1200; return w > 760 ? Math.round(Math.min(480, Math.max(380, w * 0.36))) : w }
const EASE = 'cubic-bezier(0.22, 0.8, 0.24, 1)'

/* ── the tab ──────────────────────────────────────────────────────────────── */

/** roles: array | null (null = still loading). admin: bool. onRolesChange(nextRolesArray). */
export default function RoleProfilesTab({ roles: allRoles, admin, onRolesChange }) {
  // Archived roles (a retired seat, e.g. Project Manager 2026-10-21) stay in the table as history but
  // are not on the chart.
  const roles = allRoles ? allRoles.filter(r => r.status !== 'archived') : allRoles
  const [selectedKey, setSelectedKey] = useState(null)
  const [editing, setEditing] = useState(false)
  const [team, setTeam] = useState(null)
  const [positions, setPositions] = useState({})
  const [adding, setAdding] = useState(false)
  const [peopleOpen, setPeopleOpen] = useState(false)
  const [hoverPerson, setHoverPerson] = useState(null)
  const [query, setQuery] = useState('')

  useEffect(() => {
    let live = true
    Promise.all([getTeamMembers(), getCrewRoster().catch(() => [])])
      .then(([t, roster]) => { if (live) setTeam(roleHolderTeam(t, roster)) })
      .catch(() => { if (live) setTeam([]) })
    getTeamPositions().then(p => { if (live) setPositions(p || {}) })
    return () => { live = false }
  }, [])

  // Who holds a role is set right on the sheet (admin): add from the team, remove with the ×.
  async function saveHolders(role, holders) {
    const updated = await setRoleHolders(role.role_key, holders)
    onRolesChange(allRoles.map(r => r.role_key === role.role_key ? { ...r, ...updated, holders: updated.holders || holders } : r))
  }
  async function saveTime(role, fields) {
    const updated = await updateRoleProfile(role.role_key, fields)
    onRolesChange(allRoles.map(r => r.role_key === role.role_key ? { ...r, ...updated, holders: r.holders } : r))
  }
  function select(key, opts) { setSelectedKey(key); setAdding(!!(opts && opts.add)) }

  const selected = roles && selectedKey ? roles.find(r => r.role_key === selectedKey) : null

  return (
    <div style={S.root}>
      <style>{CSS}</style>
      {roles === null ? (
        <div style={S.loading}><div className="aj-oc-pulse" style={S.loadingDot} />Loading the studio</div>
      ) : !roles.length ? (
        <div style={S.loading}>No roles yet.</div>
      ) : (
        <Canvas
          roles={roles}
          team={team}
          positions={positions}
          admin={admin}
          selectedKey={selectedKey}
          onSelect={select}
          peopleOpen={peopleOpen}
          setPeopleOpen={setPeopleOpen}
          hoverPerson={hoverPerson}
          setHoverPerson={setHoverPerson}
          query={query}
          setQuery={setQuery}
          sheet={selected && (
            <RoleSheet
              role={selected}
              roles={roles}
              admin={admin}
              team={team}
              positions={positions}
              adding={adding}
              setAdding={setAdding}
              onSaveHolders={saveHolders}
              onSaveTime={saveTime}
              onClose={() => setSelectedKey(null)}
              onEdit={() => setEditing(true)}
              onSelect={key => select(key)}
            />
          )}
          editing={editing}
        />
      )}

      {editing && selected && (
        <RoleProfileEditModal
          role={selected}
          allRoles={roles}
          onClose={() => setEditing(false)}
          onSaved={updated => { onRolesChange(allRoles.map(r => r.role_key === updated.role_key ? { ...r, ...updated } : r)); setEditing(false) }}
        />
      )}
    </div>
  )
}

/* ── canvas ───────────────────────────────────────────────────────────────── */

function Canvas({ roles, team, positions, admin, selectedKey, onSelect, peopleOpen, setPeopleOpen, hoverPerson, setHoverPerson, query, setQuery, sheet, editing }) {
  const viewRef = useRef(null)
  const searchRef = useRef(null)
  const [cam, setCam] = useState({ x: 0, y: 0, k: 0.8 })
  const camRef = useRef(cam)
  const [gliding, setGliding] = useState(false)
  const [ready, setReady] = useState(false)
  const [settled, setSettled] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [touched, setTouched] = useState(false)
  const lastSheet = useRef(null)
  if (sheet) lastSheet.current = sheet
  const movedByUser = useRef(false)
  const glideTimer = useRef(null)
  const drag = useRef(null)
  const pointers = useRef(new Map())
  const suppressClick = useRef(false)

  const { pos, childrenOf, stacks } = useMemo(() => layoutTree(roles), [roles])
  const byKey = useMemo(() => new Map(roles.map(r => [r.role_key, r])), [roles])
  const xy = useCallback(key => pos.get(key) || null, [pos])

  const bounds = useMemo(() => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const r of roles) { const p = xy(r.role_key); if (!p) continue; minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x + NODE_W); maxY = Math.max(maxY, p.y + NODE_H) }
    return { minX, minY, maxX, maxY }
  }, [roles, xy])

  const sheetOpen = !!sheet
  // Camera space the sheet takes from the canvas (0 when closed, or when it covers the whole width).
  const sheetW = useCallback(() => { const el = viewRef.current; const w = el ? el.clientWidth : 1200; return sheetOpen && w > 760 ? sheetWidthFor(el) : 0 }, [sheetOpen])

  const apply = useCallback((next, glide) => {
    camRef.current = next
    setCam(next)
    if (glide) {
      setGliding(true)
      clearTimeout(glideTimer.current)
      glideTimer.current = setTimeout(() => setGliding(false), 720)
    } else if (gliding) setGliding(false)
  }, [gliding])

  const fitTo = useCallback((box, glide = true, maxK = 1.15) => {
    const el = viewRef.current; if (!el) return
    const vw = el.clientWidth - sheetW(), vh = el.clientHeight
    const padX = 120, padTop = 130, padBottom = 110
    const w = box.maxX - box.minX, h = box.maxY - box.minY
    // Readable first: on a short window the chart may run below the fold (anchored at the top, pan to
    // the rest) rather than shrink until the cards can't be read.
    const kW = (vw - padX * 2) / w, kH = (vh - padTop - padBottom) / h
    const k = clampK(Math.min(kW, Math.max(kH, 0.62), maxK))
    const spare = vh - padTop - padBottom - h * k
    apply({ k, x: (vw - w * k) / 2 - box.minX * k, y: padTop + Math.max(0, spare / 2) - box.minY * k }, glide)
  }, [apply, sheetW])

  const fit = useCallback((glide = true) => fitTo(bounds, glide), [fitTo, bounds])

  const focusKey = useCallback(key => {
    const p = xy(key); const el = viewRef.current; if (!p || !el) return
    movedByUser.current = true; setTouched(true)
    const k = Math.max(camRef.current.k, 0.95)
    const vw = el.clientWidth - (el.clientWidth > 760 ? sheetWidthFor(el) : 0)
    apply({ k, x: vw / 2 - (p.x + NODE_W / 2) * k, y: el.clientHeight * 0.4 - (p.y + NODE_H / 2) * k }, true)
  }, [apply, xy])

  // First paint: fit, then let the nodes rise in.
  useLayoutEffect(() => {
    fit(false)
    const t1 = setTimeout(() => setReady(true), 40)
    const t2 = setTimeout(() => setSettled(true), 1600)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fit on resize until the person has moved the camera themselves.
  useEffect(() => {
    const el = viewRef.current; if (!el || typeof ResizeObserver === 'undefined') return
    let last = el.clientWidth + 'x' + el.clientHeight
    const ro = new ResizeObserver(() => {
      const now = el.clientWidth + 'x' + el.clientHeight
      if (now === last) return
      last = now
      if (!movedByUser.current) fit(false)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [fit])

  // Selecting a role glides to it; closing the sheet leaves the camera where it is.
  useEffect(() => { if (selectedKey) focusKey(selectedKey) }, [selectedKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const zoomAt = useCallback((factor, cx, cy, glide) => {
    const c = camRef.current
    const k = clampK(c.k * factor)
    const f = k / c.k
    apply({ k, x: cx - (cx - c.x) * f, y: cy - (cy - c.y) * f }, glide)
  }, [apply])

  const zoomCenter = useCallback(factor => {
    const el = viewRef.current; if (!el) return
    movedByUser.current = true
    zoomAt(factor, (el.clientWidth - sheetW()) / 2, el.clientHeight / 2, true)
  }, [zoomAt, sheetW])

  // Wheel: pinch / Ctrl / a notched mouse wheel zooms at the cursor; a trackpad scroll pans.
  useEffect(() => {
    const el = viewRef.current; if (!el) return
    function onWheel(e) {
      if (e.target.closest && e.target.closest('[data-oc-scroll]')) return
      e.preventDefault()
      movedByUser.current = true; setTouched(true)
      const rect = el.getBoundingClientRect()
      const cx = e.clientX - rect.left, cy = e.clientY - rect.top
      const notched = e.deltaMode !== 0 || (e.deltaX === 0 && Math.abs(e.deltaY) >= 40 && Number.isInteger(e.deltaY))
      if (e.ctrlKey || notched) {
        const d = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY
        zoomAt(Math.exp(-d * (e.ctrlKey ? 0.012 : 0.0016)), cx, cy, notched && !e.ctrlKey)
      } else {
        const c = camRef.current
        apply({ ...c, x: c.x - e.deltaX, y: c.y - e.deltaY }, false)
      }
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [apply, zoomAt])

  // Drag to pan (mouse, pen, one finger); two fingers pinch.
  function onPointerDown(e) {
    if (e.button !== 0 && e.pointerType === 'mouse') return
    if (e.target.closest('[data-oc-hud]')) return
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    const c = camRef.current
    if (pointers.current.size === 1) {
      drag.current = { sx: e.clientX, sy: e.clientY, cx: c.x, cy: c.y, moved: false }
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()]
      drag.current = { pinch: true, d: Math.hypot(a.x - b.x, a.y - b.y), k: c.k, cx: c.x, cy: c.y, mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2, moved: true }
    }
  }
  function onPointerMove(e) {
    if (!pointers.current.has(e.pointerId) || !drag.current) return
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    const d = drag.current
    if (d.pinch && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()]
      const rect = viewRef.current.getBoundingClientRect()
      const k = clampK(d.k * Math.hypot(a.x - b.x, a.y - b.y) / d.d)
      const f = k / d.k, mx = d.mx - rect.left, my = d.my - rect.top
      apply({ k, x: mx - (mx - d.cx) * f, y: my - (my - d.cy) * f }, false)
      return
    }
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy
    if (!d.moved && Math.hypot(dx, dy) > 4) {
      d.moved = true; movedByUser.current = true; setTouched(true); setDragging(true)
      try { viewRef.current.setPointerCapture(e.pointerId) } catch { /* fine */ }
    }
    if (d.moved) apply({ ...camRef.current, x: d.cx + dx, y: d.cy + dy }, false)
  }
  function onPointerUp(e) {
    pointers.current.delete(e.pointerId)
    if (drag.current && drag.current.moved) { suppressClick.current = true; setTimeout(() => { suppressClick.current = false }, 0) }
    if (pointers.current.size === 0) { drag.current = null; setDragging(false) }
  }

  // Keyboard.
  useEffect(() => {
    function onKey(e) {
      if (editing) return
      const tag = (document.activeElement && document.activeElement.tagName) || ''
      const typing = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
      if (e.key === 'Escape') {
        if (typing) { document.activeElement.blur(); setQuery(''); return }
        if (selectedKey) { onSelect(null); return }
        if (peopleOpen) { setPeopleOpen(false); return }
        return
      }
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomCenter(1.25) }
      else if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomCenter(0.8) }
      else if (e.key === '0') { e.preventDefault(); movedByUser.current = false; fit(true) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [editing, selectedKey, peopleOpen, onSelect, setPeopleOpen, setQuery, zoomCenter, fit])

  // People: everyone on the team plus anyone holding a role, with what they hold.
  const people = useMemo(() => {
    const map = new Map()
    for (const r of roles) for (const h of holdersOf(r)) {
      const p = map.get(h.memberId) || { id: h.memberId, name: h.name, roles: [] }
      p.roles.push(r); map.set(h.memberId, p)
    }
    for (const m of team || []) if (!map.has(m.id) && m.name) map.set(m.id, { id: m.id, name: m.name, roles: [] })
    return [...map.values()].sort((a, b) => (b.roles.length ? 1 : 0) - (a.roles.length ? 1 : 0) || a.name.localeCompare(b.name))
  }, [roles, team])

  const openSeats = roles.filter(r => !holdersOf(r).length).length

  // What is lit: a search, a hovered person, or the selected role's line.
  const q = query.trim().toLowerCase()
  const lit = useMemo(() => {
    if (q) return new Set(roles.filter(r => r.role_title.toLowerCase().includes(q) || holdersOf(r).some(h => h.name.toLowerCase().includes(q))).map(r => r.role_key))
    if (hoverPerson) return new Set(roles.filter(r => holdersOf(r).some(h => h.memberId === hoverPerson)).map(r => r.role_key))
    if (selectedKey) {
      const s = new Set([selectedKey])
      let cur = byKey.get(selectedKey), guard = 0
      while (cur && cur.reports_to && byKey.has(cur.reports_to) && guard++ < 20) { s.add(cur.reports_to); cur = byKey.get(cur.reports_to) }
      for (const c of childrenOf.get(selectedKey) || []) s.add(c.role_key)
      for (const k of (byKey.get(selectedKey) || {}).direction_from || []) s.add(k)
      for (const r of roles) if ((r.direction_from || []).includes(selectedKey)) s.add(r.role_key)
      return s
    }
    return null
  }, [q, hoverPerson, selectedKey, roles, byKey, childrenOf])

  function submitSearch(e) {
    e.preventDefault()
    const first = roles.find(r => lit && lit.has(r.role_key))
    if (first) { onSelect(first.role_key); searchRef.current && searchRef.current.blur() }
  }

  function showPerson(p) {
    if (!p.roles.length) return
    if (p.roles.length === 1) { onSelect(p.roles[0].role_key); return }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const r of p.roles) { const c = xy(r.role_key); if (!c) continue; minX = Math.min(minX, c.x); minY = Math.min(minY, c.y); maxX = Math.max(maxX, c.x + NODE_W); maxY = Math.max(maxY, c.y + NODE_H) }
    movedByUser.current = true
    fitTo({ minX, minY, maxX, maxY }, true, 1)
  }

  // Connectors: a soft curve from each parent to each child; a stacked column hangs off a spine.
  const links = []
  for (const [parentKey, kids] of childrenOf.entries()) {
    if (!parentKey) continue
    const a = xy(parentKey); if (!a) continue
    const stack = stacks.get(parentKey)
    const x1 = a.x + NODE_W / 2, y1 = a.y + NODE_H
    for (const kid of kids) {
      const b = xy(kid.role_key); if (!b) continue
      const on = !!(lit && lit.has(parentKey) && lit.has(kid.role_key))
      if (stack && stack.keys.includes(kid.role_key)) {
        const sx = stack.spineX, top = y1 + GAP_Y * 0.5, cy = b.y + NODE_H / 2
        const d = `M${x1},${y1} C${x1},${y1 + GAP_Y * 0.32} ${sx},${top - GAP_Y * 0.22} ${sx},${top} L${sx},${cy - 12} Q${sx},${cy} ${sx + 12},${cy} L${b.x},${cy}`
        links.push({ key: parentKey + '>' + kid.role_key, d, on, x2: b.x, y2: cy })
      } else {
        const x2 = b.x + NODE_W / 2, y2 = b.y, my = (y1 + y2) / 2
        links.push({ key: parentKey + '>' + kid.role_key, d: `M${x1},${y1} C${x1},${my} ${x2},${my} ${x2},${y2}`, on, x2, y2 })
      }
    }
  }
  // Dotted lines: a role taking direction from another besides the one it reports to (database/320).
  // From the side of the director's card to the top of the role's, so it never runs over the solid tree.
  for (const r of roles) {
    for (const fromKey of r.direction_from || []) {
      const a = xy(fromKey), b = xy(r.role_key)
      if (!a || !b || fromKey === r.reports_to) continue
      const right = b.x + NODE_W / 2 >= a.x + NODE_W / 2
      const x1 = right ? a.x + NODE_W : a.x, y1 = a.y + NODE_H / 2
      const above = b.y + NODE_H < a.y
      const x2 = b.x + NODE_W / 2, y2 = above ? b.y + NODE_H : b.y
      const d = above
        ? `M${x1},${y1} C${x1 + (right ? 1 : -1) * GAP_X * 1.4},${y1} ${x2},${y2 + GAP_Y * 0.6} ${x2},${y2}`
        : `M${x1},${y1} C${x1 + (right ? 1 : -1) * GAP_X * 1.4},${y1} ${x2},${y2 - GAP_Y * 0.6} ${x2},${y2}`
      const on = !!(lit && lit.has(fromKey) && lit.has(r.role_key))
      links.push({ key: fromKey + '~>' + r.role_key, d, on, x2, y2, dotted: true })
    }
  }
  // Lit links draw last so they sit on top.
  links.sort((p, q) => (p.on ? 1 : 0) - (q.on ? 1 : 0))
  // Card text grows a little when zoomed out so the chart stays readable from far away.
  const textScale = Math.min(1.25, Math.max(1, 0.72 / cam.k))

  const grid = 26 * cam.k
  const transition = gliding ? `transform 700ms ${EASE}` : 'none'
  const bgTransition = gliding ? `background-position 700ms ${EASE}, background-size 700ms ${EASE}` : 'none'

  return (
    <div
      ref={viewRef}
      className={'aj-oc-view' + (dragging ? ' is-dragging' : '')}
      style={{ ...S.view, backgroundSize: `${grid}px ${grid}px`, backgroundPosition: `${cam.x}px ${cam.y}px`, transition: bgTransition }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onDoubleClick={e => { if (!e.target.closest('[data-oc-node],[data-oc-hud]')) { movedByUser.current = false; fit(true) } }}
    >
      <div style={S.vignette} />

      <div style={{ ...S.world, transform: `translate(${cam.x}px, ${cam.y}px) scale(${cam.k})`, transition, '--oc-ts': textScale }}>
        <svg style={S.svg} width="1" height="1" overflow="visible">
          {links.map(l => (
            <g key={l.key}>
              <path d={l.d} className={'aj-oc-link' + (l.dotted ? ' is-dotted' : '') + (l.on ? ' is-on' : '') + (lit && !l.on ? ' is-dim' : '')} vectorEffect="non-scaling-stroke" />
              {!l.dotted && <circle cx={l.x2} cy={l.y2} r={l.on ? 3.5 : 2.5} className={'aj-oc-joint' + (l.on ? ' is-on' : '') + (lit && !l.on ? ' is-dim' : '')} />}
            </g>
          ))}
        </svg>

        {roles.map((r, i) => {
          const p = xy(r.role_key); if (!p) return null
          const depth = p.depth
          return (
            <RoleNode
              key={r.role_key}
              role={r}
              left={p.x}
              top={p.y}
              delay={settled ? 0 : depth * 80 + (i % 8) * 24}
              ready={ready}
              selected={r.role_key === selectedKey}
              dim={!!lit && !lit.has(r.role_key)}
              admin={admin}
              onClick={() => { if (suppressClick.current) return; onSelect(r.role_key) }}
              onAdd={() => { if (suppressClick.current) return; onSelect(r.role_key, { add: true }) }}
            />
          )
        })}
      </div>

      {/* Top-left: title, counts, find. */}
      <div data-oc-hud style={S.hudTopLeft} className="aj-oc-glass aj-oc-rise">
        <div style={S.hudTitleRow}>
          <span style={S.hudTitle}>Org Chart</span>
          <span style={S.hudCount}>{roles.length} roles{openSeats ? ` · ${openSeats} open` : ''}</span>
        </div>
        <form onSubmit={submitSearch} style={S.searchWrap}>
          <svg width="14" height="14" viewBox="0 0 16 16" style={S.searchIcon}><circle cx="7" cy="7" r="5" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M11 11l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
          <input ref={searchRef} value={query} onChange={e => setQuery(e.target.value)} placeholder="Find a role or person" style={S.search} />
          {query && lit && !lit.size && <span style={S.kbd}>No match</span>}
        </form>
      </div>

      {/* Top-right: People. */}
      <div data-oc-hud style={{ ...S.hudTopRight, right: sheetOpen ? sheetW() + 20 : 20 }} className="aj-oc-rise">
        <button className={'aj-oc-glass aj-oc-pill' + (peopleOpen ? ' is-on' : '')} onClick={() => setPeopleOpen(v => !v)}>
          <svg width="15" height="15" viewBox="0 0 16 16"><circle cx="6" cy="5.5" r="2.6" fill="none" stroke="currentColor" strokeWidth="1.4" /><path d="M1.5 13.5c.6-2.4 2.4-3.6 4.5-3.6s3.9 1.2 4.5 3.6" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /><circle cx="11.6" cy="6" r="2" fill="none" stroke="currentColor" strokeWidth="1.3" /><path d="M11.3 9.9c1.7.1 2.8 1.1 3.3 3" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>
          People
        </button>
      </div>

      {peopleOpen && (
        <div data-oc-hud data-oc-scroll style={{ ...S.people, right: sheetOpen ? sheetW() + 20 : 20 }} className="aj-oc-glass aj-oc-drop">
          <div style={S.peopleHead}>The team</div>
          {team === null && <div style={S.peopleNote}>Loading…</div>}
          {people.map(p => (
            <button
              key={p.id}
              className="aj-oc-person"
              onMouseEnter={() => setHoverPerson(p.id)}
              onMouseLeave={() => setHoverPerson(null)}
              onClick={() => showPerson(p)}
            >
              <Avatar name={p.name} id={p.id} size={30} />
              <span style={S.personText}>
                <span style={S.personName}>{p.name}</span>
                {positions[p.id] && <span style={S.personPosition}>{positions[p.id].name}</span>}
                <span style={p.roles.length ? S.personRoles : S.personFree}>
                  {p.roles.length ? p.roles.map(r => r.role_title).join(' · ') : 'No role yet'}
                </span>
                {p.roles.length > 0 && <WeekBar week={weekOf(p.roles)} />}
              </span>
              {p.roles.length > 1 && <span style={S.personCount}>{p.roles.length}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Bottom-right: zoom. */}
      <div data-oc-hud style={{ ...S.hudBottomRight, right: sheetOpen ? sheetW() + 20 : 20 }} className="aj-oc-glass aj-oc-rise">
        <button className="aj-oc-icon" onClick={() => zoomCenter(0.8)} title="Zoom out (−)">
          <svg width="14" height="14" viewBox="0 0 14 14"><path d="M3 7h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
        </button>
        <span style={S.zoomPct}>{Math.round(cam.k * 100)}%</span>
        <button className="aj-oc-icon" onClick={() => zoomCenter(1.25)} title="Zoom in (+)">
          <svg width="14" height="14" viewBox="0 0 14 14"><path d="M3 7h8M7 3v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
        </button>
        <span style={S.hudDivider} />
        <button className="aj-oc-icon aj-oc-fit" onClick={() => { movedByUser.current = false; fit(true) }} title="Fit everything (0)">
          <svg width="14" height="14" viewBox="0 0 14 14"><path d="M1.5 5V1.5H5M9 1.5h3.5V5M12.5 9v3.5H9M5 12.5H1.5V9" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
          Fit
        </button>
      </div>

      {!touched && !sheetOpen && (
        <div style={S.hint} className="aj-oc-hint">Drag to move · Scroll or pinch to zoom · Click a role to open it</div>
      )}

      <div data-oc-hud data-oc-scroll className={'aj-oc-sheet' + (sheet ? ' is-open' : '')} style={{ width: sheetWidthFor(viewRef.current) }}>
        {sheet || lastSheet.current}
      </div>
    </div>
  )
}

/* ── a person's week: roles, reserve, what is left for planes ─────────────── */

function WeekBar({ week }) {
  const pct = h => Math.max(0, Math.min(100, h / WEEK_H * 100))
  const over = week.left < 0
  return (
    <span style={{ display: 'block', marginTop: 5 }} title={`Roles ${hrs(week.planned)} a week · reserve ${week.reservePct}% (${hrs(week.reserve)}) · ${over ? 'over by ' + hrs(-week.left) : hrs(week.left) + ' left after roles'}`}>
      <span style={{ display: 'flex', height: 4, borderRadius: 2, overflow: 'hidden', background: 'var(--aj-bg-subtle)' }}>
        <span style={{ width: pct(week.planned) + '%', background: over ? '#c0392b' : 'var(--aj-text-mid)' }} />
        <span style={{ width: pct(week.reserve) + '%', background: over ? 'rgba(192,57,43,0.45)' : 'var(--aj-border-mid)' }} />
      </span>
      <span style={{ display: 'block', fontFamily: MONO, fontSize: 10.5, marginTop: 3, color: over ? '#c0392b' : 'var(--aj-text-muted)' }}>
        {over ? `Over by ${hrs(-week.left)} a week` : `${hrs(week.left)} a week left after roles`}
      </span>
    </span>
  )
}

/* ── node ─────────────────────────────────────────────────────────────────── */

function RoleNode({ role, left, top, delay, ready, selected, dim, admin, onClick, onAdd }) {
  const holders = holdersOf(role)
  const open = !holders.length
  const cls = ['aj-oc-node', open && 'is-open-seat', selected && 'is-selected', dim && 'is-dim', ready && 'is-ready'].filter(Boolean).join(' ')
  return (
    <button
      data-oc-node
      className={cls}
      style={{ left, top, width: NODE_W, height: NODE_H, transitionDelay: delay ? `${delay}ms, 0ms, ${delay}ms, 0ms, 0ms` : undefined }}
      onClick={onClick}
    >
      <div style={S.nodeTop}>
        {open ? (
          <span
            className={admin ? 'aj-oc-add' : undefined}
            style={S.openRing}
            role={admin ? 'button' : undefined}
            title={admin ? 'Add a person to this role' : undefined}
            onClick={admin ? (e => { e.stopPropagation(); onAdd() }) : undefined}
          >
            <svg width="12" height="12" viewBox="0 0 12 12"><path d="M6 2.5v7M2.5 6h7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>
          </span>
        ) : (
          <span style={S.avatarStack}>
            {holders.slice(0, 3).map((h, i) => <span key={h.memberId} style={{ marginLeft: i ? -8 : 0, zIndex: 3 - i, position: 'relative' }}><Avatar name={h.name} id={h.memberId} size={28} ring /></span>)}
          </span>
        )}
        <span style={{ display: 'inline-flex', gap: 4 }}>
          {role.status === 'draft' && <span style={S.draftChip}>Draft</span>}
        </span>
      </div>
      <div style={S.nodeTitle}>{role.role_title}</div>
      <div style={open ? S.nodeOpen : S.nodeHolder}>
        {open ? 'Open seat' : holders.map(h => firstName(h.name)).join(' · ')}
      </div>
    </button>
  )
}

const firstName = n => String(n || '').split(/\s+/)[0]

function Avatar({ name, id, size = 28, ring }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: '50%', background: toneFor(id || name), color: '#fff',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none',
      fontFamily: '"DM Sans",sans-serif', fontWeight: 600, fontSize: Math.round(size * 0.38), letterSpacing: '0.02em',
      boxShadow: ring ? '0 0 0 2px var(--aj-bg-elevated)' : 'none',
    }}>{initials(name)}</span>
  )
}

/* ── sheet: one role's profile ────────────────────────────────────────────── */

function RoleSheet({ role, roles, admin, team, positions, adding, setAdding, onSaveHolders, onSaveTime, onClose, onEdit, onSelect }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  async function change(next) {
    setBusy(true); setErr('')
    try { await onSaveHolders(role, next) } catch (e) { setErr(e.message || 'Could not save.') } finally { setBusy(false) }
  }
  const parent = role.reports_to ? roles.find(r => r.role_key === role.reports_to) : null
  const reports = roles.filter(r => r.reports_to === role.role_key).sort((a, b) => a.sort_order - b.sort_order)
  const directionFrom = (role.direction_from || []).map(k => roles.find(r => r.role_key === k)).filter(Boolean)
  const directs = roles.filter(r => (r.direction_from || []).includes(role.role_key))
  const holders = holdersOf(role)
  const meta = [FOCUS_LABEL[role.flight_focus], COORD_LABEL[role.coordinates]].filter(Boolean)

  return (
    <div key={role.role_key} className="aj-oc-sheet-inner">
      <div style={S.sheetBar}>
        {admin ? <button className="aj-oc-text-btn" onClick={onEdit}>Edit</button> : <span />}
        <button className="aj-oc-close" onClick={onClose} title="Close (Esc)">
          <svg width="12" height="12" viewBox="0 0 12 12"><path d="M2.5 2.5l7 7M9.5 2.5l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
        </button>
      </div>

      <div style={S.sheetHead}>
        <div style={S.sheetEyebrow}>{parent ? 'Reports to ' + parent.role_title : 'Top of the studio'}</div>
        <div style={S.sheetTitle}>{role.role_title}</div>
        <div style={{ ...S.sheetHolders, opacity: busy ? 0.55 : 1 }}>
          {holders.map(h => (
            <span key={h.memberId} style={S.holderChip} title={positions && positions[h.memberId] ? positions[h.memberId].name : undefined}>
              <Avatar name={h.name} id={h.memberId} size={24} />
              <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
                {h.name}
                {positions && positions[h.memberId] && <span style={S.holderPosition}>{positions[h.memberId].name}</span>}
              </span>
              {admin && <button className="aj-oc-chip-x" disabled={busy} title={'Remove ' + h.name + ' from this role'} onClick={() => change(holders.filter(x => x.memberId !== h.memberId))}>
                <svg width="9" height="9" viewBox="0 0 10 10"><path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
              </button>}
            </span>
          ))}
          {!holders.length && !admin && <span style={S.openChip}>Open seat</span>}
          {admin && (
            <button className={'aj-oc-add-chip' + (adding ? ' is-on' : '')} onClick={() => setAdding(!adding)}>
              <svg width="11" height="11" viewBox="0 0 12 12"><path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
              {holders.length ? 'Add person' : 'Fill this seat'}
            </button>
          )}
        </div>
        {admin && adding && (
          <div style={S.picker} className="aj-oc-drop">
            <div style={S.pickerHead}>Add someone to {role.role_title}</div>
            {team === null && <div style={S.peopleNote}>Loading the team…</div>}
            {(team || []).filter(m => m.name && !holders.some(h => h.memberId === m.id)).map(m => (
              <button key={m.id} className="aj-oc-person" disabled={busy}
                onClick={async () => { await change([...holders, { memberId: m.id, name: m.name }]); setAdding(false) }}>
                <Avatar name={m.name} id={m.id} size={26} />
                <span style={S.personText}>
                  <span style={S.personName}>{m.name}</span>
                  {positions && positions[m.id] && <span style={S.personPosition}>{positions[m.id].name}</span>}
                </span>
              </button>
            ))}
            <div style={S.pickerFoot}>People who don't take crew seats (Admin → Crew) aren't listed.</div>
          </div>
        )}
        {err && <div style={{ ...S.note, color: '#b3261e', marginTop: 10, marginBottom: 0 }}>{err}</div>}
        {meta.length > 0 && <div style={S.sheetMeta}>{meta.join(' · ')}</div>}
      </div>

      {role.status === 'draft' && <div style={S.note}>Draft. This profile is a starting point and still needs review.</div>}
      {isReconstructedIncomplete(role) && <div style={S.note}>Some sections of this profile are still being written.</div>}

      {role.purpose_statement && <p style={S.purpose}>{role.purpose_statement}</p>}

      <RoleTime role={role} roles={roles} admin={admin} onSave={fields => onSaveTime(role, fields)} />

      {(parent || reports.length > 0 || directionFrom.length > 0 || directs.length > 0) && (
        <Section title="In the studio">
          {parent && (
            <div style={S.relRow}><span style={S.relLabel}>Reports to</span>
              <button className="aj-oc-rel" onClick={() => onSelect(parent.role_key)}>{parent.role_title}</button>
            </div>
          )}
          {directionFrom.length > 0 && (
            <div style={S.relRow}><span style={S.relLabel}>Takes direction from</span>
              <span style={S.relList}>{directionFrom.map(r => <button key={r.role_key} className="aj-oc-rel is-dotted" onClick={() => onSelect(r.role_key)}>{r.role_title}</button>)}</span>
            </div>
          )}
          {reports.length > 0 && (
            <div style={S.relRow}><span style={S.relLabel}>Direct reports</span>
              <span style={S.relList}>{reports.map(r => <button key={r.role_key} className="aj-oc-rel" onClick={() => onSelect(r.role_key)}>{r.role_title}</button>)}</span>
            </div>
          )}
          {directs.length > 0 && (
            <div style={S.relRow}><span style={S.relLabel}>Gives direction to</span>
              <span style={S.relList}>{directs.map(r => <button key={r.role_key} className="aj-oc-rel is-dotted" onClick={() => onSelect(r.role_key)}>{r.role_title}</button>)}</span>
            </div>
          )}
        </Section>
      )}

      {!isEmpty(role.responsibilities) && (
        <Section title="Responsibilities">
          {role.responsibilities.map((g, i) => (
            <div key={i} style={S.domain}>
              <div style={S.domainLabel}>{g.domain}</div>
              {(g.items || []).map((it, j) => <div key={j} style={S.bullet}><span style={S.bulletDot} />{it}</div>)}
            </div>
          ))}
        </Section>
      )}

      {!isEmpty(role.decision_authority) && (
        <Section title="Decisions">
          {role.decision_authority.map((d, i) => (
            <div key={i} style={S.decision}>
              <div style={S.decisionMain}>{d.role_decides}</div>
              {d.involves_president && <div style={S.decisionUp}><span style={S.upArrow}>↑</span>{parent ? parent.role_title + ': ' : ''}{d.involves_president}</div>}
            </div>
          ))}
        </Section>
      )}

      {!isEmpty(role.operational_rhythm) && (
        <Section title="Rhythm">
          {role.operational_rhythm.map((r, i) => (
            <div key={i} style={S.rhythm}>
              <span style={S.rhythmWhen}>{r.cadence}</span>
              <span style={S.rhythmWhat}>{r.purpose}</span>
            </div>
          ))}
        </Section>
      )}

      {!isEmpty(role.success_metrics) && (
        <Section title="What success looks like">
          <div style={S.metricGrid}>
            {role.success_metrics.map((m, i) => (
              <div key={i} style={S.metric}>
                <div style={S.metricDomain}>{m.domain}</div>
                <div style={S.metricText}>{m.fully_executing}</div>
                {m.kpi && <div style={S.metricKpi}>{m.kpi}</div>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {!isEmpty(role.qualifications) && (
        <Section title="Qualifications">
          {role.qualifications.map((q, i) => <div key={i} style={S.bullet}><span style={S.bulletDot} />{q}</div>)}
        </Section>
      )}

      {!isEmpty(role.boundaries) && (
        <Section title="Boundaries">
          {role.boundaries.map((b, i) => <div key={i} style={{ ...S.bullet, color: 'var(--aj-text-mid)' }}><span style={{ ...S.bulletDot, background: 'var(--aj-text-muted)' }} />{b}</div>)}
        </Section>
      )}

      {(role.callouts || []).map((c, i) => <div key={i} style={S.callout}>{c}</div>)}
      <div style={{ height: 40 }} />
    </div>
  )
}

/* The role's time, and how each holder's week adds up with it (database/328). Admin edits in place. */
function RoleTime({ role, roles, admin, onSave }) {
  const initPer = w => (w && w % 5 === 0 ? 'day' : 'week')
  const weekly = Number(role.focus_hours_per_week) || 0
  const [triage, setTriage] = useState(role.triage_minutes ?? '')
  const [per, setPer] = useState(initPer(weekly))
  const [focus, setFocus] = useState(weekly ? String(initPer(weekly) === 'day' ? weekly / 5 : weekly) : '')
  const [reserve, setReserve] = useState(role.reserve_pct ?? '')
  const [state, setState] = useState('')
  useEffect(() => {
    const w = Number(role.focus_hours_per_week) || 0
    setTriage(role.triage_minutes ?? ''); setPer(initPer(w)); setFocus(w ? String(initPer(w) === 'day' ? w / 5 : w) : ''); setReserve(role.reserve_pct ?? ''); setState('')
  }, [role.role_key, role.triage_minutes, role.focus_hours_per_week, role.reserve_pct])

  const num = v => (v === '' || v == null ? null : Number(v))
  async function save(next) {
    const f = num(focus), pr = next.per || per
    const fields = { triage_minutes: num(triage), focus_hours_per_week: f == null ? null : Math.round(f * (pr === 'day' ? 5 : 1) * 100) / 100, reserve_pct: num(reserve) }
    const same = fields.triage_minutes === (role.triage_minutes ?? null) && fields.reserve_pct === (role.reserve_pct ?? null)
      && fields.focus_hours_per_week === (role.focus_hours_per_week == null ? null : Number(role.focus_hours_per_week))
    if (same) return
    setState('Saving…')
    try { await onSave(fields); setState('Saved') } catch (e) { setState(e.message || 'Could not save.') }
  }

  const holders = holdersOf(role)
  return (
    <Section title="Time">
      <div style={S.timeGrid}>
        <label style={S.timeRow}><span style={S.relLabel}>Daily triage</span>
          {admin ? <span><input style={S.timeInput} type="number" min="0" max="480" step="5" value={triage} placeholder="–" onChange={e => setTriage(e.target.value)} onBlur={() => save({})} /> min a day</span>
            : <span>{role.triage_minutes == null ? 'Not set' : role.triage_minutes + ' min a day'}</span>}
        </label>
        <label style={S.timeRow}><span style={S.relLabel}>Focus block</span>
          {admin ? <span><input style={S.timeInput} type="number" min="0" max="60" step="0.5" value={focus} placeholder="–" onChange={e => setFocus(e.target.value)} onBlur={() => save({})} /> h a{' '}
            <select style={S.timeSelect} value={per} onChange={e => { setPer(e.target.value); save({ per: e.target.value }) }}><option value="day">day</option><option value="week">week</option></select></span>
            : <span>{weekly ? (weekly % 5 === 0 ? weekly / 5 + ' h a day' : weekly + ' h a week') : 'Not set'}</span>}
        </label>
        <label style={S.timeRow}><span style={S.relLabel}>Reserve</span>
          {admin ? <span><input style={S.timeInput} type="number" min="0" max="80" step="5" value={reserve} placeholder="–" onChange={e => setReserve(e.target.value)} onBlur={() => save({})} /> % of the week</span>
            : <span>{role.reserve_pct == null ? 'Not set' : role.reserve_pct + '% of the week'}</span>}
        </label>
      </div>
      <div style={S.timeNote}>Reserve is unplanned time for emergent work. A person takes the largest reserve among their roles, not the sum. A week is {WEEK_H} hours.{state ? ' · ' + state : ''}</div>
      {holders.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {holders.map(h => {
            const theirs = roles.filter(r => holdersOf(r).some(x => x.memberId === h.memberId))
            return (
              <div key={h.memberId} style={S.timeHolder}>
                <span style={{ fontWeight: 600 }}>{h.name}</span>
                <span style={{ fontSize: 11.5, color: 'var(--aj-text-muted)' }}>{theirs.length} role{theirs.length === 1 ? '' : 's'}</span>
                <span style={{ gridColumn: '1 / -1' }}><WeekBar week={weekOf(theirs)} /></span>
              </div>
            )
          })}
        </div>
      )}
    </Section>
  )
}

function Section({ title, children }) {
  return (
    <div style={S.section}>
      <div style={S.sectionTitle}>{title}</div>
      {children}
    </div>
  )
}

/* ── styles ───────────────────────────────────────────────────────────────── */

const SANS = '"DM Sans",sans-serif'
const MONO = '"DM Mono",monospace'
const DISPLAY = 'Montserrat,sans-serif'
const GLASS_BG = 'color-mix(in srgb, var(--aj-bg-elevated) 82%, transparent)'

const CSS = `
.aj-oc-view { cursor: grab; touch-action: none; user-select: none; -webkit-user-select: none; }
.aj-oc-view.is-dragging { cursor: grabbing; }
.aj-oc-glass {
  background: ${GLASS_BG};
  backdrop-filter: saturate(1.6) blur(18px); -webkit-backdrop-filter: saturate(1.6) blur(18px);
  border: 0.5px solid color-mix(in srgb, var(--aj-border-mid) 70%, transparent);
  box-shadow: 0 1px 1px rgba(7,39,45,0.04), 0 10px 30px -12px rgba(7,39,45,0.22);
}
.aj-oc-node {
  position: absolute; box-sizing: border-box; text-align: left; cursor: pointer;
  padding: 14px 16px 12px; border-radius: 18px; overflow: hidden;
  background: ${GLASS_BG};
  backdrop-filter: saturate(1.5) blur(14px); -webkit-backdrop-filter: saturate(1.5) blur(14px);
  border: 0.5px solid color-mix(in srgb, var(--aj-border-mid) 80%, transparent);
  box-shadow: 0 1px 1px rgba(7,39,45,0.05), 0 12px 32px -14px rgba(7,39,45,0.28);
  opacity: 0; transform: translateY(14px) scale(0.96);
  transition: transform 620ms ${EASE}, box-shadow 380ms ${EASE}, opacity 520ms ${EASE}, border-color 240ms ease, filter 300ms ease;
}
.aj-oc-node.is-ready { opacity: 1; transform: none; }
.aj-oc-node.is-ready:hover { transform: translateY(-3px); box-shadow: 0 2px 2px rgba(7,39,45,0.05), 0 22px 44px -16px rgba(7,39,45,0.34); }
.aj-oc-node:focus-visible { outline: none; box-shadow: 0 0 0 3px color-mix(in srgb, var(--aj-accent) 40%, transparent), 0 12px 32px -14px rgba(7,39,45,0.28); }
.aj-oc-node.is-selected {
  border-color: color-mix(in srgb, var(--aj-accent) 70%, transparent);
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--aj-accent) 55%, transparent), 0 0 0 7px color-mix(in srgb, var(--aj-accent) 10%, transparent), 0 24px 48px -16px rgba(7,39,45,0.36);
}
.aj-oc-node.is-open-seat { background: color-mix(in srgb, var(--aj-bg-elevated) 45%, transparent); border-style: dashed; box-shadow: none; }
.aj-oc-node.is-ready.is-dim { opacity: 0.28; filter: saturate(0.4); }
.aj-oc-link { fill: none; stroke: var(--aj-border-mid); stroke-width: 1.25; transition: stroke 300ms ease, opacity 300ms ease, stroke-width 300ms ease; }
.aj-oc-link.is-on { stroke: var(--aj-accent); stroke-width: 2; stroke-dasharray: 6 6; animation: aj-oc-flow 1.6s linear infinite; }
.aj-oc-link.is-dotted { stroke-dasharray: 2 5; stroke-linecap: round; stroke-width: 1.6; }
.aj-oc-link.is-dotted.is-on { stroke-dasharray: 2 5; animation: none; }
.aj-oc-link.is-dim, .aj-oc-joint.is-dim { opacity: 0.25; }
.aj-oc-joint { fill: var(--aj-bg-elevated); stroke: var(--aj-border-mid); stroke-width: 1.25; vector-effect: non-scaling-stroke; transition: opacity 300ms ease; }
.aj-oc-joint.is-on { fill: var(--aj-accent); stroke: var(--aj-accent); }
@keyframes aj-oc-flow { to { stroke-dashoffset: -24; } }
.aj-oc-rise { animation: aj-oc-rise 560ms ${EASE} both 120ms; }
@keyframes aj-oc-rise { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
.aj-oc-drop { animation: aj-oc-drop 320ms ${EASE} both; }
@keyframes aj-oc-drop { from { opacity: 0; transform: translateY(-8px) scale(0.98); } to { opacity: 1; transform: none; } }
.aj-oc-pill {
  display: inline-flex; align-items: center; gap: 7px; height: 36px; padding: 0 15px; border-radius: 18px; cursor: pointer;
  font-family: ${SANS}; font-size: 13px; font-weight: 600; color: var(--aj-text);
  transition: transform 200ms ${EASE}, background 200ms ease;
}
.aj-oc-pill:hover { transform: translateY(-1px); }
.aj-oc-pill.is-on { background: var(--aj-text); color: var(--aj-bg-elevated); }
.aj-oc-icon {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-width: 32px; height: 32px; padding: 0 8px;
  border: none; background: none; border-radius: 10px; cursor: pointer; color: var(--aj-text);
  font-family: ${SANS}; font-size: 12.5px; font-weight: 600; transition: background 160ms ease;
}
.aj-oc-icon:hover { background: color-mix(in srgb, var(--aj-text) 7%, transparent); }
.aj-oc-person {
  display: flex; align-items: center; gap: 11px; width: 100%; padding: 8px 10px; border: none; background: none;
  border-radius: 12px; cursor: pointer; text-align: left; transition: background 160ms ease;
}
.aj-oc-person:hover { background: color-mix(in srgb, var(--aj-text) 6%, transparent); }
.aj-oc-hint { animation: aj-oc-hint 7s ease both 900ms; }
@keyframes aj-oc-hint { 0% { opacity: 0; transform: translate(-50%, 6px); } 8%, 82% { opacity: 1; transform: translate(-50%, 0); } 100% { opacity: 0; } }
.aj-oc-sheet {
  position: absolute; top: 0; right: 0; bottom: 0; overflow-y: auto; overscroll-behavior: contain;
  background: ${GLASS_BG};
  backdrop-filter: saturate(1.6) blur(26px); -webkit-backdrop-filter: saturate(1.6) blur(26px);
  border-left: 0.5px solid color-mix(in srgb, var(--aj-border-mid) 70%, transparent);
  box-shadow: -24px 0 60px -30px rgba(7,39,45,0.35);
  transform: translateX(104%); transition: transform 520ms ${EASE}; cursor: auto; user-select: text; -webkit-user-select: text; touch-action: pan-y;
}
.aj-oc-sheet.is-open { transform: none; }
.aj-oc-sheet-inner { padding: 18px 30px 0; animation: aj-oc-fade 420ms ${EASE} both; }
@keyframes aj-oc-fade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
.aj-oc-close, .aj-oc-text-btn {
  border: none; cursor: pointer; font-family: ${SANS}; color: var(--aj-text);
  background: color-mix(in srgb, var(--aj-text) 7%, transparent); transition: background 160ms ease;
}
.aj-oc-close { width: 28px; height: 28px; border-radius: 14px; display: inline-flex; align-items: center; justify-content: center; }
.aj-oc-text-btn { height: 28px; padding: 0 14px; border-radius: 14px; font-size: 12.5px; font-weight: 600; }
.aj-oc-close:hover, .aj-oc-text-btn:hover { background: color-mix(in srgb, var(--aj-text) 13%, transparent); }
.aj-oc-rel {
  border: 0.5px solid var(--aj-border-mid); background: var(--aj-bg-surface); color: var(--aj-text); cursor: pointer;
  font-family: ${SANS}; font-size: 12.5px; font-weight: 500; padding: 5px 11px; border-radius: 14px; transition: border-color 160ms ease, transform 160ms ${EASE};
}
.aj-oc-rel:hover { border-color: var(--aj-accent); transform: translateY(-1px); }
.aj-oc-rel.is-dotted { border-style: dashed; }
.aj-oc-add { cursor: pointer; transition: background 160ms ease, color 160ms ease, border-color 160ms ease, transform 160ms ${EASE}; }
.aj-oc-add:hover { background: var(--aj-accent); color: #fff; border-style: solid; border-color: var(--aj-accent); transform: scale(1.08); }
.aj-oc-add-chip {
  display: inline-flex; align-items: center; gap: 6px; height: 32px; padding: 0 13px; border-radius: 16px; cursor: pointer;
  border: 1px dashed var(--aj-border-mid); background: none; color: var(--aj-text-mid);
  font-family: ${SANS}; font-size: 12.5px; font-weight: 600; transition: border-color 160ms ease, color 160ms ease, background 160ms ease;
}
.aj-oc-add-chip:hover, .aj-oc-add-chip.is-on { border-style: solid; border-color: var(--aj-accent); color: var(--aj-accent); }
.aj-oc-chip-x {
  display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px; margin-left: 2px; border-radius: 10px;
  border: none; background: none; color: var(--aj-text-muted); cursor: pointer; transition: background 140ms ease, color 140ms ease;
}
.aj-oc-chip-x:hover { background: color-mix(in srgb, #b3261e 12%, transparent); color: #b3261e; }
.aj-oc-pulse { animation: aj-oc-pulse 1.2s ease-in-out infinite; }
@keyframes aj-oc-pulse { 0%, 100% { opacity: 0.3; transform: scale(0.85); } 50% { opacity: 1; transform: scale(1); } }
@media (prefers-reduced-motion: reduce) {
  .aj-oc-node { transition: none !important; }
  .aj-oc-rise, .aj-oc-drop, .aj-oc-sheet-inner, .aj-oc-hint, .aj-oc-link.is-on { animation: none !important; opacity: 1; transform: none; }
}
`

const S = {
  root: { position: 'absolute', inset: 0, overflow: 'hidden', background: 'var(--aj-bg-page)' },
  loading: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, fontFamily: SANS, fontSize: 13, color: 'var(--aj-text-muted)' },
  loadingDot: { width: 8, height: 8, borderRadius: 4, background: 'var(--aj-accent)' },

  view: {
    position: 'absolute', inset: 0, overflow: 'hidden',
    backgroundColor: 'var(--aj-bg-page)',
    backgroundImage: 'radial-gradient(circle, color-mix(in srgb, var(--aj-text-muted) 32%, transparent) 1px, transparent 1.4px)',
  },
  vignette: { position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse at 50% 40%, transparent 55%, color-mix(in srgb, var(--aj-bg-page) 85%, transparent) 100%)' },
  world: { position: 'absolute', left: 0, top: 0, transformOrigin: '0 0', willChange: 'transform' },
  svg: { position: 'absolute', left: 0, top: 0, overflow: 'visible', pointerEvents: 'none' },

  nodeTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 28, marginBottom: 9 },
  avatarStack: { display: 'inline-flex', alignItems: 'center' },
  openRing: { width: 28, height: 28, borderRadius: '50%', border: '1px dashed var(--aj-text-muted)', color: 'var(--aj-text-muted)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  draftChip: { fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 10, padding: '2px 7px' },
  nodeTitle: { fontFamily: SANS, fontSize: 'calc(15px * var(--oc-ts, 1))', lineHeight: 1.15, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--aj-text)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  nodeHolder: { fontFamily: SANS, fontSize: 'calc(12.5px * min(var(--oc-ts, 1), 1.3))', color: 'var(--aj-text-mid)', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  nodeOpen: { fontFamily: SANS, fontSize: 'calc(12.5px * min(var(--oc-ts, 1), 1.3))', color: 'var(--aj-text-muted)', marginTop: 3 },

  hudTopLeft: { position: 'absolute', top: 20, left: 20, borderRadius: 18, padding: '14px 14px 12px', width: 280 },
  hudTitleRow: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0 4px 10px' },
  hudTitle: { fontFamily: DISPLAY, fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  hudCount: { fontFamily: MONO, fontSize: 11, color: 'var(--aj-text-muted)' },
  searchWrap: { position: 'relative', display: 'flex', alignItems: 'center', margin: 0 },
  searchIcon: { position: 'absolute', left: 11, color: 'var(--aj-text-muted)', pointerEvents: 'none' },
  search: {
    width: '100%', boxSizing: 'border-box', height: 34, padding: '0 30px 0 32px', borderRadius: 11,
    border: '0.5px solid var(--aj-border)', background: 'color-mix(in srgb, var(--aj-bg-page) 70%, transparent)',
    fontFamily: SANS, fontSize: 13, color: 'var(--aj-text)', outline: 'none',
  },
  kbd: { position: 'absolute', right: 9, fontFamily: MONO, fontSize: 10.5, color: 'var(--aj-text-muted)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 5, padding: '0 5px', lineHeight: '16px' },

  hudTopRight: { position: 'absolute', top: 20, transition: `right 520ms ${EASE}` },
  people: { position: 'absolute', top: 66, width: 300, maxHeight: 'calc(100% - 150px)', overflowY: 'auto', borderRadius: 18, padding: '12px 8px', transition: `right 520ms ${EASE}` },
  peopleHead: { fontFamily: DISPLAY, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', padding: '2px 10px 8px' },
  peopleNote: { fontFamily: SANS, fontSize: 12.5, color: 'var(--aj-text-muted)', padding: '6px 10px' },
  personText: { display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 },
  personName: { fontFamily: SANS, fontSize: 13.5, fontWeight: 600, color: 'var(--aj-text)' },
  personRoles: { fontFamily: SANS, fontSize: 11.5, color: 'var(--aj-text-mid)', marginTop: 1, lineHeight: 1.35 },
  personFree: { fontFamily: SANS, fontSize: 11.5, color: 'var(--aj-accent)', marginTop: 1, fontWeight: 600 },
  personPosition: { fontFamily: MONO, fontSize: 10.5, color: 'var(--aj-text-muted)', marginTop: 1 },
  holderPosition: { fontFamily: MONO, fontSize: 10, fontWeight: 400, color: 'var(--aj-text-muted)', marginTop: 2 },
  picker: { marginTop: 12, border: '0.5px solid var(--aj-border)', borderRadius: 14, background: 'var(--aj-bg-surface)', padding: '8px 6px', maxHeight: 320, overflowY: 'auto' },
  pickerHead: { fontFamily: DISPLAY, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', padding: '4px 10px 8px' },
  pickerFoot: { fontFamily: SANS, fontSize: 11.5, color: 'var(--aj-text-muted)', padding: '8px 10px 4px' },
  personCount: { fontFamily: MONO, fontSize: 11, color: 'var(--aj-text-muted)', minWidth: 20, textAlign: 'center' },

  hudBottomRight: { position: 'absolute', bottom: 20, display: 'flex', alignItems: 'center', gap: 2, padding: 4, borderRadius: 14, transition: `right 520ms ${EASE}` },
  zoomPct: { fontFamily: MONO, fontSize: 11.5, color: 'var(--aj-text-mid)', minWidth: 44, textAlign: 'center' },
  hudDivider: { width: 0.5, height: 18, background: 'var(--aj-border-mid)', margin: '0 4px' },
  hint: { position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)', fontFamily: SANS, fontSize: 12.5, color: 'var(--aj-text-mid)', background: GLASS_BG, border: '0.5px solid var(--aj-border)', borderRadius: 16, padding: '7px 14px', pointerEvents: 'none', whiteSpace: 'nowrap' },

  sheet: {},
  sheetBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, padding: '4px 0 10px', zIndex: 1 },
  sheetHead: { padding: '6px 0 18px' },
  sheetEyebrow: { fontFamily: MONO, fontSize: 11, color: 'var(--aj-text-muted)', marginBottom: 8 },
  sheetTitle: { fontFamily: DISPLAY, fontSize: 24, fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color: 'var(--aj-text)', lineHeight: 1.15 },
  sheetHolders: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 },
  holderChip: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '3px 12px 3px 3px', borderRadius: 16, background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border)', fontFamily: SANS, fontSize: 13, fontWeight: 500, color: 'var(--aj-text)' },
  openChip: { display: 'inline-flex', alignItems: 'center', padding: '5px 12px', borderRadius: 16, border: '1px dashed var(--aj-border-mid)', fontFamily: SANS, fontSize: 13, color: 'var(--aj-text-muted)' },
  sheetMeta: { fontFamily: SANS, fontSize: 12.5, color: 'var(--aj-text-mid)', marginTop: 12 },
  note: { fontFamily: SANS, fontSize: 12.5, color: 'var(--aj-text-mid)', background: 'color-mix(in srgb, var(--aj-text) 5%, transparent)', borderRadius: 12, padding: '9px 12px', marginBottom: 18 },
  purpose: { fontFamily: SANS, fontSize: 17, lineHeight: 1.55, letterSpacing: '-0.005em', color: 'var(--aj-text)', margin: '0 0 26px' },

  section: { padding: '18px 0 6px', borderTop: '0.5px solid var(--aj-border)' },
  timeGrid: { display: 'grid', gap: 8 },
  timeRow: { display: 'grid', gridTemplateColumns: '120px minmax(0,1fr)', alignItems: 'center', gap: 10, fontFamily: SANS, fontSize: 13, color: 'var(--aj-text)' },
  timeInput: { width: 64, fontFamily: MONO, fontSize: 12.5, padding: '4px 6px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', marginRight: 4 },
  timeSelect: { fontFamily: SANS, fontSize: 12.5, padding: '3px 4px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)' },
  timeNote: { fontFamily: SANS, fontSize: 11.5, color: 'var(--aj-text-muted)', marginTop: 10, lineHeight: 1.45 },
  timeHolder: { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: '2px 10px', alignItems: 'baseline', fontFamily: SANS, fontSize: 13, padding: '8px 0', borderTop: '0.5px solid var(--aj-border)' },
  sectionTitle: { fontFamily: DISPLAY, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 14 },
  relRow: { display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 12 },
  relLabel: { fontFamily: SANS, fontSize: 12.5, color: 'var(--aj-text-muted)', width: 124, flex: 'none', paddingTop: 5 },
  relList: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  domain: { marginBottom: 16 },
  domainLabel: { fontFamily: SANS, fontSize: 13.5, fontWeight: 600, color: 'var(--aj-text)', marginBottom: 6 },
  bullet: { display: 'flex', gap: 10, fontFamily: SANS, fontSize: 14, lineHeight: 1.5, color: 'var(--aj-text)', marginBottom: 7 },
  bulletDot: { width: 5, height: 5, borderRadius: 3, background: 'var(--aj-accent)', flex: 'none', marginTop: 8 },
  decision: { padding: '10px 0', borderBottom: '0.5px solid var(--aj-border)' },
  decisionMain: { fontFamily: SANS, fontSize: 14, lineHeight: 1.45, color: 'var(--aj-text)' },
  decisionUp: { fontFamily: SANS, fontSize: 12.5, lineHeight: 1.45, color: 'var(--aj-text-mid)', marginTop: 4 },
  upArrow: { fontFamily: MONO, marginRight: 6, color: 'var(--aj-text-muted)' },
  rhythm: { display: 'flex', gap: 14, padding: '9px 0', borderBottom: '0.5px solid var(--aj-border)' },
  rhythmWhen: { fontFamily: MONO, fontSize: 11.5, color: 'var(--aj-text-mid)', width: 112, flex: 'none', paddingTop: 2 },
  rhythmWhat: { fontFamily: SANS, fontSize: 14, lineHeight: 1.45, color: 'var(--aj-text)' },
  metricGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 },
  metric: { background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border)', borderRadius: 14, padding: '12px 14px' },
  metricDomain: { fontFamily: SANS, fontSize: 12.5, fontWeight: 600, color: 'var(--aj-text)', marginBottom: 5 },
  metricText: { fontFamily: SANS, fontSize: 13, lineHeight: 1.45, color: 'var(--aj-text-mid)' },
  metricKpi: { fontFamily: MONO, fontSize: 11, color: 'var(--aj-accent)', marginTop: 8 },
  callout: { fontFamily: SANS, fontSize: 14, lineHeight: 1.55, color: 'var(--aj-text)', background: 'color-mix(in srgb, var(--aj-accent) 7%, transparent)', borderRadius: 14, padding: '13px 16px', margin: '14px 0 0' },
}
